import React, {useEffect, useState} from "react";
import FormControl from "@mui/material/FormControl";
import {makeStyles} from "tss-react/mui";

import Check from "@mui/icons-material/Check";
import Warning from "@mui/icons-material/Warning";
import Button from "../CustomButtons/Button";
import SnackbarContent from "../Snackbar/SnackbarContent";

import {UserForRegistration, useStudentVerifyStep3Request,} from "../../request/api";

import styles from "../../styles/jss/nextjs-material-kit/components/modalForms.js";
import {FormHelperText} from "@mui/material";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {PATHWAYS} from "../RegisterForm/StudentRegisterForm";
import {useAuthContext} from "../../contexts/auth";

const useStyles = makeStyles()(styles);

export default function VerifyStudentStep3Form({
  hash,
  verifyData,
  isLoading,
  triggerNextStep,
  userVerifyError,
  closeFromInside,
}) {
  /**
   * @type {UserForRegistration}
   */
  const [registration, setRegistration] = useState({
    career_zones: [],
    pathway: verifyData.pathway,
  });

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const { refreshUser } = useAuthContext();

  const [errorMessage, setErrorMessage] = useState(null);

  const { classes } = useStyles();
  const { isMutating, trigger: save } = useStudentVerifyStep3Request({
    key: hash,
  });

  useEffect(() => {
    window.gtag && window.gtag("event", "student_verify_step3_form");
  }, []);

  const handleInputChange = (event) => {
    setRegistration((registration) => ({
      ...registration,
      [event.target.name.replace("register_", "")]: event.target.value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setErrors({});
    setErrorMessage("");

    try {
      const response = await save({
        body: registration,
      });

      window.gtag && window.gtag("event", "student_verify_step3_submit");

      if (response.data.success) {
        setSuccess(true);

        await refreshUser();

        triggerNextStep();
      } else {
        setErrorMessage(response.data.error);
      }
    } catch (e) {
      console.error(e);
      setErrorMessage(
        e.response?.data?.errors?.length > 0
          ? e.response?.data?.errors[0]
          : e.response?.data?.message || e.message
      );
      setErrors(e.response?.data?.errors);
    }
  };

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={submit}>
        <SnackbarContent
          message="Finally, please choose three areas you're interested in so we can match you with the right opportunities."
          color="success"
          icon={Check}
        />
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <label htmlFor="career_zones" className={classes.label}>
                Career Zones *
              </label>
              <Select
                fullWidth
                required
                id="career_zones"
                name="career_zones"
                value={registration.career_zones}
                onChange={handleInputChange}
                multiple
              >
                {(verifyData?.careerZones ?? []).map(({ id, name }) => (
                  <MenuItem
                    key={id}
                    value={id}
                    disabled={
                      registration.career_zones.length >= 3 &&
                      !registration.career_zones.includes(id)
                    }
                  >
                    {name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={!!errors?.career_zones}>
                {errors?.career_zones}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <label htmlFor="pathway" className={classes.label}>
                What pathway are you currently most likely to choose? *
              </label>
              <Select
                fullWidth
                required
                id="pathway"
                name="pathway"
                value={registration?.pathway}
                onChange={handleInputChange}
              >
                {PATHWAYS.map(value => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
              <FormHelperText error={!!errors?.pathway}>
                {errors?.pathway}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {errorMessage ? (
              <SnackbarContent
                message={errorMessage}
                color="warning"
                icon={Warning}
              />
            ) : (
              <>
                <div>* required field</div>
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              type={"submit"}
              disabled={isMutating}
              fullWidth
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
}
